import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Building2, Save, Upload, Trash2, Globe, Phone, Mail, MapPin, Clock, DollarSign, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { businessSettingsAPI } from '@/services/api';
import DataResetDangerZone from '@/components/hq/DataResetDangerZone';
import GRNSelectiveDeletePanel from '@/components/hq/GRNSelectiveDeletePanel';

const timezones = [
  { value: 'Asia/Kolkata', label: 'India (IST) - Asia/Kolkata' },
  { value: 'Asia/Dubai', label: 'UAE (GST) - Asia/Dubai' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT) - Asia/Singapore' },
  { value: 'Europe/London', label: 'UK (GMT/BST) - Europe/London' },
  { value: 'America/New_York', label: 'US Eastern - America/New_York' },
  { value: 'America/Los_Angeles', label: 'US Pacific - America/Los_Angeles' }
];

const currencies = [
  { value: 'INR', symbol: '₹', label: 'Indian Rupee (₹)' },
  { value: 'USD', symbol: '$', label: 'US Dollar ($)' },
  { value: 'EUR', symbol: '€', label: 'Euro (€)' },
  { value: 'GBP', symbol: '£', label: 'British Pound (£)' },
  { value: 'AED', symbol: 'د.إ', label: 'UAE Dirham (د.إ)' },
  { value: 'SGD', symbol: 'S$', label: 'Singapore Dollar (S$)' }
];

const BusinessSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [settings, setSettings] = useState({
    organization_name: '',
    legal_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    phone: '',
    alternate_phone: '',
    email: '',
    website: '',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    currency_symbol: '₹',
    gstin: '',
    pan: '',
    logo_url: '',
    tagline: '',
    support_hours: '',
    facebook_url: '',
    instagram_url: '',
    youtube_url: '',
    twitter_url: '',
    linkedin_url: '',
    whatsapp_number: '',
  });
  
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await businessSettingsAPI.get();
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleCurrencyChange = (currencyValue) => {
    const currency = currencies.find(c => c.value === currencyValue);
    setSettings(prev => ({
      ...prev,
      currency: currencyValue,
      currency_symbol: currency?.symbol || '₹'
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await businessSettingsAPI.update(settings);
      toast({ title: 'Success', description: 'Settings saved successfully' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Error', description: 'Invalid file type. Allowed: PNG, JPEG, WebP', variant: 'destructive' });
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'Error', description: 'File too large. Max 2MB allowed', variant: 'destructive' });
      return;
    }
    
    setUploadingLogo(true);
    try {
      const response = await businessSettingsAPI.uploadLogo(file);
      setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast({ title: 'Success', description: 'Logo uploaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to upload logo', variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!confirm('Delete the organization logo?')) return;
    
    try {
      await businessSettingsAPI.deleteLogo();
      setSettings(prev => ({ ...prev, logo_url: '' }));
      toast({ title: 'Success', description: 'Logo deleted' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete logo', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="business-settings-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Business Settings</h1>
          <p className="text-gray-500">Configure your organization details</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings} className="border-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-teal-500 hover:bg-teal-600 text-white"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Organization Details */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-500" />
                Organization Details
              </CardTitle>
              <CardDescription>Basic information about your organization</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Organization Name *</Label>
                  <Input
                    value={settings.organization_name}
                    onChange={(e) => handleChange('organization_name', e.target.value)}
                    placeholder="SY-Store"
                    className="bg-gray-50 border-gray-300"
                    data-testid="org-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Legal Name</Label>
                  <Input
                    value={settings.legal_name}
                    onChange={(e) => handleChange('legal_name', e.target.value)}
                    placeholder="SY-Store Pvt Ltd"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">GSTIN</Label>
                  <Input
                    value={settings.gstin}
                    onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
                    placeholder="22AAAAA0000A1Z5"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">PAN</Label>
                  <Input
                    value={settings.pan}
                    onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                    placeholder="AAAAA0000A"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <Phone className="w-5 h-5 text-teal-500" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Phone Number</Label>
                  <Input
                    value={settings.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+91 9876543210"
                    className="bg-gray-50 border-gray-300"
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Alternate Phone</Label>
                  <Input
                    value={settings.alternate_phone}
                    onChange={(e) => handleChange('alternate_phone', e.target.value)}
                    placeholder="+91 9876543211"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Email</Label>
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="contact@systore.com"
                    className="bg-gray-50 border-gray-300"
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Website</Label>
                  <Input
                    value={settings.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://www.systore.com"
                    className="bg-gray-50 border-gray-300"
                    data-testid="website-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-teal-500" />
                Address
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Address Line 1</Label>
                <Input
                  value={settings.address_line1}
                  onChange={(e) => handleChange('address_line1', e.target.value)}
                  placeholder="123 Main Street"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Address Line 2</Label>
                <Input
                  value={settings.address_line2}
                  onChange={(e) => handleChange('address_line2', e.target.value)}
                  placeholder="Near City Center"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">City</Label>
                  <Input
                    value={settings.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Chennai"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">State</Label>
                  <Input
                    value={settings.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="Tamil Nadu"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Country</Label>
                  <Input
                    value={settings.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    placeholder="India"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Pincode</Label>
                  <Input
                    value={settings.pincode}
                    onChange={(e) => handleChange('pincode', e.target.value)}
                    placeholder="600001"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Logo */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800">Organization Logo</CardTitle>
              <CardDescription>PNG, JPEG or WebP (max 2MB)</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center">
                {settings.logo_url ? (
                  <div className="relative mb-4">
                    <img 
                      src={settings.logo_url} 
                      alt="Logo" 
                      className="w-32 h-32 object-contain border border-gray-200 rounded-lg"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        // If CDN URL fails, show placeholder
                        console.error('Logo failed to load:', settings.logo_url?.substring(0, 50));
                        e.target.style.display = 'none';
                        e.target.nextSibling?.classList?.remove('hidden');
                      }}
                    />
                    <div className="hidden w-32 h-32 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500 text-center p-2">
                      Logo uploaded but preview unavailable (CDN)
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleDeleteLogo}
                      className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 hover:bg-red-600 text-white rounded-full"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-4">
                    <Building2 className="w-12 h-12 text-gray-300" />
                  </div>
                )}
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                />
                
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="w-full border-gray-300"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Regional Settings */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4 border-b border-gray-100">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <Globe className="w-5 h-5 text-teal-500" />
                Regional Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Timezone
                </Label>
                <Select value={settings.timezone} onValueChange={(v) => handleChange('timezone', v)}>
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {timezones.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Currency
                </Label>
                <Select value={settings.currency} onValueChange={handleCurrencyChange}>
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {currencies.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="bg-teal-50 p-3 rounded-lg">
                <p className="text-sm text-teal-700">
                  <strong>Date Format:</strong> dd/MM/yyyy hh:mm AM/PM
                </p>
                <p className="text-xs text-teal-600 mt-1">
                  Example: 17/01/2026 03:30 PM
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {/* Storefront + Socials */}
        <Card className="bg-white border-gray-200 shadow-sm" data-testid="socials-card">
          <CardHeader className="pb-4 border-b border-gray-100">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <Globe className="w-5 h-5 text-pink-500" />
              Storefront & Social Media
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">Shown on the public website — footer, contact page, and marketing sections.</p>
          </CardHeader>
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Tagline (optional)</Label>
              <Input
                value={settings.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="e.g. Eleven Icons. Seven Rituals. One Sanctuary."
                data-testid="tagline-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Support Hours</Label>
              <Input
                value={settings.support_hours}
                onChange={(e) => handleChange('support_hours', e.target.value)}
                placeholder="Mon – Sat · 10 AM to 7 PM"
                data-testid="support-hours-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Facebook page URL</Label>
              <Input
                value={settings.facebook_url}
                onChange={(e) => handleChange('facebook_url', e.target.value)}
                placeholder="https://facebook.com/your.brand"
                data-testid="facebook-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Instagram profile URL</Label>
              <Input
                value={settings.instagram_url}
                onChange={(e) => handleChange('instagram_url', e.target.value)}
                placeholder="https://instagram.com/your.brand"
                data-testid="instagram-input"
              />
            </div>
            <div className="space-y-2">
              <Label>YouTube channel URL</Label>
              <Input
                value={settings.youtube_url}
                onChange={(e) => handleChange('youtube_url', e.target.value)}
                placeholder="https://youtube.com/@yourbrand"
                data-testid="youtube-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Twitter / X URL</Label>
              <Input
                value={settings.twitter_url}
                onChange={(e) => handleChange('twitter_url', e.target.value)}
                placeholder="https://twitter.com/yourbrand"
                data-testid="twitter-input"
              />
            </div>
            <div className="space-y-2">
              <Label>LinkedIn URL</Label>
              <Input
                value={settings.linkedin_url}
                onChange={(e) => handleChange('linkedin_url', e.target.value)}
                placeholder="https://linkedin.com/company/yourbrand"
                data-testid="linkedin-input"
              />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp number</Label>
              <Input
                value={settings.whatsapp_number}
                onChange={(e) => handleChange('whatsapp_number', e.target.value)}
                placeholder="+91 98765 43210"
                data-testid="whatsapp-input"
              />
            </div>
          </CardContent>
        </Card>

        <DataResetDangerZone />
        <GRNSelectiveDeletePanel />
      </div>
    </div>
  );
};

export default BusinessSettingsPage;
